import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { shiftAPI } from '@matix/api-client';
import type { MyShiftListItemDto } from '@matix/api-client';
import { formatDateTime } from '@matix/utils';
import {
  DateTimeRange, ExcelButton, fmtMoney, fmtInt,
  todayFrom, todayTo, rangeParams, rangeLabel,
} from './reportShared';
import { ReportSheetBuilder, downloadReportExcel } from './reportExcel';
import { useLang } from '../../lib/language';
import { useUI } from '../../store/ui';

const PAGE_SIZE = 20;

/**
 * Reports → My Shifts. The cashier's own sessions (every drawer they ran),
 * newest first, filtered on when they were OPENED — today by default — and
 * paginated. The filter is a datetime at each end, as every report here is:
 * a till that opened at 23:50 belongs to the evening the supervisor is
 * looking at, not to the calendar day it lands in.
 *
 * "Export to Excel" writes the CURRENT PAGE, which is what is on screen —
 * the page size is the cashier's own choice of how much to look at, and a
 * spreadsheet that silently held more rows than the screen did would be the
 * surprising thing.
 *
 * Each CLOSED shift offers its Z-report through the same modal the close
 * flow shows, so a reprint is the same preview and the same slip as the
 * original. An open shift has no snapshot yet (the close writes it), so the
 * button is deliberately absent rather than disabled — there is nothing to
 * print until the till is closed.
 */
export function MyShiftsReport(): JSX.Element {
  const { t, lang } = useLang();
  const showModal = useUI((s) => s.showModal);

  const [from, setFrom] = useState(todayFrom);
  const [to, setTo] = useState(todayTo);
  const [page, setPage] = useState(1);
  const [params, setParams] = useState(() => rangeParams(todayFrom(), todayTo())!);

  const q = useQuery({
    queryKey: ['my-shifts', params, page],
    queryFn: () => shiftAPI.list({ ...params, page, limit: PAGE_SIZE }),
    placeholderData: (prev) => prev,
  });

  function run(): void {
    const r = rangeParams(from, to);
    if (!r) return;
    setPage(1);
    setParams(r);
  }

  function openZReport(shift: MyShiftListItemDto): void {
    // Same staging the close screen uses — the modal reads the id from here,
    // and offers both the roll and an A4 sheet.
    (window as unknown as { __pos_zreport_shift_id?: number }).__pos_zreport_shift_id = shift.id;
    showModal('zreport');
  }

  const d = q.data;
  const totalPages = d ? Math.max(1, Math.ceil(d.total / d.limit)) : 1;
  const title = t({ en: 'My Shifts', ar: 'ورديّاتي' });
  // The range the LISTED rows came from, not whatever the pickers hold now.
  const range = rangeLabel(
    `${params.from}T${params.from_time ?? '00:00'}`,
    `${params.to}T${params.to_time ?? '23:59'}`,
  );

  function exportExcel(): void {
    if (!d || d.data.length === 0) return;
    const rows = d.data;
    const sheet = new ReportSheetBuilder()
      .title(title, `${range} · ${t({ en: 'Page', ar: 'صفحة' })} ${page} / ${totalPages}`)
      .table(
        [
          { label: t({ en: 'Shift', ar: 'الوردية' }) },
          { label: t({ en: 'Status', ar: 'الحالة' }), kind: 'center' },
          { label: t({ en: 'Branch', ar: 'الفرع' }) },
          { label: t({ en: 'Drawer', ar: 'الدرج' }) },
          { label: t({ en: 'Opened', ar: 'الفتح' }), kind: 'center' },
          { label: t({ en: 'Closed', ar: 'الإغلاق' }), kind: 'center' },
          { label: t({ en: 'Receipts', ar: 'الفواتير' }), kind: 'int' },
          { label: t({ en: 'Net sales', ar: 'صافي المبيعات' }), kind: 'money' },
          { label: t({ en: 'Expected', ar: 'المتوقع' }), kind: 'money' },
          { label: t({ en: 'Counted', ar: 'المعدود' }), kind: 'money' },
          { label: t({ en: 'Variance', ar: 'الفرق' }), kind: 'money' },
        ],
        rows.map((sh) => [
          sh.shift_code ?? `#${sh.id}`,
          sh.status === 'open' ? t({ en: 'Open', ar: 'مفتوحة' }) : t({ en: 'Closed', ar: 'مغلقة' }),
          sh.branch_name ?? '',
          sh.drawer_name ?? '',
          formatDateTime(sh.opened_at),
          sh.closed_at ? formatDateTime(sh.closed_at) : '',
          sh.receipt_count,
          sh.receipts_net,
          sh.expected_total,
          sh.counted_total,
          sh.variance,
        ]),
        {
          totals: (() => {
            // An OPEN shift has no expected, counted or variance yet, so it
            // contributes nothing to those three — the totals are over the
            // shifts that have been closed and counted, not over the page.
            const sum = (pick: (s: (typeof rows)[number]) => number | null) =>
              rows.reduce((acc, sh) => acc + (pick(sh) ?? 0), 0);
            return [
              t({ en: 'Total', ar: 'الإجمالي' }), null, null, null, null, null,
              sum((sh) => sh.receipt_count),
              sum((sh) => sh.receipts_net),
              sum((sh) => sh.expected_total),
              sum((sh) => sh.counted_total),
              sum((sh) => sh.variance),
            ];
          })(),
        },
      )
      .build(title, { rightToLeft: lang === 'ar' });
    downloadReportExcel(`${title} ${range}`, sheet);
  }

  return (
    <div className="rpt-wrap">
      <div className="rpt-toolbar">
        <div className="rpt-filters">
          <DateTimeRange from={from} to={to} onFrom={setFrom} onTo={setTo} />
        </div>
        <div className="rpt-actions">
          <button type="button" className="rpt-btn rpt-btn-primary" onClick={run} disabled={q.isFetching}>
            {q.isFetching ? t({ en: 'Loading…', ar: 'جارٍ التحميل…' }) : t({ en: 'Show shifts', ar: 'عرض الورديات' })}
          </button>
          {d && d.data.length > 0 && <ExcelButton onClick={exportExcel} />}
        </div>
      </div>

      {q.error && (
        <div className="rpt-error">{t({ en: 'Failed to load shifts. Try again.', ar: 'تعذّر تحميل الورديات. حاول مجدداً.' })}</div>
      )}

      <div className="rpt-print">
        <div className="rpt-meta">
          {range}
          {d && ` · ${t({ en: 'Shifts', ar: 'الورديات' })}: ${fmtInt(d.total)}`}
        </div>

        <div className="rpt-table-scroll">
          <table>
            <thead>
              <tr>
                <th>{t({ en: 'Shift', ar: 'الوردية' })}</th>
                <th>{t({ en: 'Status', ar: 'الحالة' })}</th>
                <th>{t({ en: 'Branch / Drawer', ar: 'الفرع / الدرج' })}</th>
                <th>{t({ en: 'Opened', ar: 'الفتح' })}</th>
                <th>{t({ en: 'Closed', ar: 'الإغلاق' })}</th>
                <th className="num">{t({ en: 'Receipts', ar: 'الفواتير' })}</th>
                <th className="num">{t({ en: 'Net sales', ar: 'صافي المبيعات' })}</th>
                <th className="num">{t({ en: 'Expected', ar: 'المتوقع' })}</th>
                <th className="num">{t({ en: 'Counted', ar: 'المعدود' })}</th>
                <th className="num">{t({ en: 'Variance', ar: 'الفرق' })}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {d && d.data.length === 0 && (
                <tr>
                  <td colSpan={11} className="rpt-empty">
                    {t({ en: 'No shifts in this period.', ar: 'لا توجد ورديات في هذه الفترة.' })}
                  </td>
                </tr>
              )}
              {d?.data.map((s) => {
                const canPrintZ = s.status === 'closed' && s.has_z_report;
                return (
                  <tr key={s.id}>
                    <td>
                      <b>{s.shift_code ?? `#${s.id}`}</b>
                      {s.shift_code && <span className="rpt-sub"> · #{s.id}</span>}
                    </td>
                    <td>
                      <span className={`rpt-badge ${s.status === 'open' ? 'rpt-badge-open' : 'rpt-badge-closed'}`}>
                        {s.status === 'open' ? t({ en: 'Open', ar: 'مفتوحة' }) : t({ en: 'Closed', ar: 'مغلقة' })}
                      </span>
                    </td>
                    <td>
                      {s.branch_name ?? '—'}
                      {s.drawer_name && <span className="rpt-sub"> · {s.drawer_name}</span>}
                    </td>
                    <td dir="ltr" className="rpt-nowrap">{formatDateTime(s.opened_at)}</td>
                    <td dir="ltr" className="rpt-nowrap">{s.closed_at ? formatDateTime(s.closed_at) : '—'}</td>
                    <td className="num">{fmtInt(s.receipt_count)}</td>
                    <td className="num">{fmtMoney(s.receipts_net)}</td>
                    <td className="num">{s.expected_total != null ? fmtMoney(s.expected_total) : '—'}</td>
                    <td className="num">{s.counted_total != null ? fmtMoney(s.counted_total) : '—'}</td>
                    <td className={`num${s.variance != null && s.variance !== 0 ? ' rpt-variance' : ''}`}>
                      {s.variance != null ? fmtMoney(s.variance) : '—'}
                    </td>
                    <td className="rpt-nowrap">
                      {canPrintZ && (
                        <button type="button" className="rpt-btn rpt-btn-sm" onClick={() => openZReport(s)}>
                          {t({ en: 'Z-Report', ar: 'تقرير Z' })}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {d && d.total > d.limit && (
          <div className="rpt-pager">
            <button
              type="button"
              className="rpt-btn rpt-btn-sm"
              disabled={page <= 1 || q.isFetching}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              {t({ en: 'Previous', ar: 'السابق' })}
            </button>
            <span className="rpt-pager-info">
              {t({ en: 'Page', ar: 'صفحة' })} {page} / {totalPages}
            </span>
            <button
              type="button"
              className="rpt-btn rpt-btn-sm"
              disabled={page >= totalPages || q.isFetching}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              {t({ en: 'Next', ar: 'التالي' })}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
